import _ from "lodash";
import React, { useRef, useState } from "react";
import { useSelected, useFocused, useSlate, ReactEditor } from "slate-react";
import classes from "./Element.scss";
import { IconButton } from "@toddle-edu/ds-web";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { DeleteOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";

import { connect } from "react-redux";
import { openLinkInNewTab } from "modules/Services";

import AddEditLinkModal from "../AddEditLinkModal";
import { deleteLink } from "../InlineEditorUtils";
import classNames from "classnames";
import ImageResizer from "./ImageResizer";
import { Editor, Transforms } from "slate";

const Element = props => {
  const { attributes, children, element } = props;

  const textElementClassName = classNames({
    [classes.textRightAlignment]: _.get(attributes, "dir") === "rtl",
    [classes.textElementClasses]: true,
  });

  switch (element.type) {
    case "image":
      return <Image {...props} />;
    case "link":
      return <Link {...props} />;
    default:
      return (
        <p className={textElementClassName} {...attributes}>
          {children}
        </p>
      );
  }
};

/**
 *
 * @param { attributes, children, element }
 * @returns Inline Editor Image component
 */
const Image = ({
  element,
  children,
  attributes,
  isUploading,
  onImageResize,
}) => {
  const imageRef = useRef(null);
  const selected = useSelected();
  const focused = useFocused();
  const isSelected = selected && focused;

  const style = {};
  if (element?.width) style.width = `${element.width}px`;

  const imageClassName = classNames(classes.image, {
    [classes.imageFocused]: isSelected,
  });

  const handleImageResize = size => {
    onImageResize(_.get(element, "id"), size);
  };

  return (
    <div {...attributes}>
      {children}
      <div contentEditable={false} className={classes.imageContainer}>
        <img
          style={style}
          ref={imageRef}
          src={element.url}
          alt={element.url}
          className={imageClassName}
        />
        {isSelected && !isUploading && imageRef.current && (
          <ImageResizer imageRef={imageRef} onResizeEnd={handleImageResize} />
        )}
      </div>
    </div>
  );
};

Image.defaultProps = {
  isUploading: false,
  onImageResize: () => {},
};

const linkPopoverContainerStyles = {
  zIndex: 1100,
  backgroundColor: "var(--surface-primary-enabled)",
  borderRadius: "8px",
  boxShadow: "0 5px 10px rgba(0, 0, 0, 0.3)",
  maxWidth: "360px",
  top: "2px",
  width: "100%",
};

const Link = ({
  attributes,
  children,
  element,
  isDisabled,
  openLinkInNewTab,
}) => {
  const [showEditLinkModal, setShowEditLinkModal] = useState(false);
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const editor = useSlate();
  // reference passed for Popover positioning
  const linkContainerRef = useRef();

  const onLinkTextClick = e => {
    if (isDisabled) {
      // if disabled, open link in new tab
      e.preventDefault();
      openLinkInNewTab({ url: element.url });
    } else setShowLinkPopover(true);
  };
  const closePopover = () => {
    setShowLinkPopover(false);
  };
  const openEditLinkModal = () => {
    setShowEditLinkModal(true);
    closePopover();
  };
  const closeEditLinkModal = () => {
    setShowEditLinkModal(false);
    // Move the selection to the end of the editor
    Transforms.select(editor, Editor.end(editor, []));
    // Focus the editor
    ReactEditor.focus(editor);
  };

  const handleDeleteLinkClick = () => {
    deleteLink({ editor, linkElement: element });
  };

  return (
    <>
      <a
        {...attributes}
        href={element.url}
        ref={linkContainerRef}
        onClick={onLinkTextClick}
        className={classes.urlElement}
      >
        {children}
      </a>
      <Popover
        isOpen={showLinkPopover}
        align={"start"}
        positions={["bottom"]}
        reposition={true}
        isChildrenRefPassed={true}
        childrenRef={linkContainerRef}
        containerStyle={linkPopoverContainerStyles}
        onClickOutside={closePopover}
        content={() => (
          <div
            className={classNames("text-body-1", classes.linkIconsContainer)}
          >
            <a
              className={classes.urlElementWithWidth}
              href={element.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {element.url}
            </a>
            <div className={classes.iconsContainer}>
              <IconButton
                icon={<PencilLineOutlined variant="subtle" />}
                dsVersion="2.0"
                size="medium"
                variant="neutral"
                type="plain"
                onClick={openEditLinkModal}
              />
              <IconButton
                icon={<DeleteOutlined variant="subtle" />}
                dsVersion="2.0"
                size="medium"
                variant="neutral"
                type="plain"
                onClick={handleDeleteLinkClick}
              />
            </div>
          </div>
        )}
      />
      {showEditLinkModal ? (
        <AddEditLinkModal onClose={closeEditLinkModal} linkElement={element} />
      ) : null}
    </>
  );
};
const mapActionCreators = {
  openLinkInNewTab,
};
export default connect(null, mapActionCreators)(Element);
